import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LetModule } from '@ngrx/component';
import { TranslateModule } from '@ngx-translate/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  mergeMap,
  skipWhile,
} from 'rxjs';

import { AssetsModule, icons } from '@tsq-web/assets';
import { updateQueryParamsV2 } from '@tsq-web/router';

import { PaginationItem } from '../../models/pagination-item';
import { calculateVisiblePaginationItems } from '../../utils/_pagination';
import { pageIndexFromParams } from '../../utils/params';

@UntilDestroy()
@Component({
  standalone: true,
  imports: [AsyncPipe, NgFor, NgIf, LetModule, TranslateModule, AssetsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'tsq-pagination',
  template: `
    <nav
      *ngIf="(numberOfPages$ | async) > 1"
      [attr.aria-label]="'LIBS.PAGINATION.ARIA_LABELS.NAV' | translate"
      [attr.schema]="schema"
    >
      <ul
        *ngrxLet="{
          currentPageIndex: currentPageIndex$,
          numberOfPages: numberOfPages$,
          visibleItems: visibleItems$,
        } as vm"
        class="flex items-center gap-4"
      >
        <li>
          <button
            [attr.aria-label]="'LIBS.PAGINATION.ARIA_LABELS.PREVIOUS_PAGE' | translate"
            [disabled]="vm.currentPageIndex <= 0"
            (click)="onPreviousClick(vm.currentPageIndex)"
          >
            <tsq-icon [icon]="chevronIcon" classes="size-24 rotate-180" />
          </button>
        </li>

        <ng-container *ngFor="let item of vm.visibleItems; trackBy: trackByPaginationItem">
          <li>
            <button
              *ngIf="item.type === 'page'; else ellipses"
              [attr.aria-label]="
                'LIBS.PAGINATION.ARIA_LABELS.' +
                  (vm.currentPageIndex + 1 === item.value ? 'CURRENT_PAGE' : 'GO_TO_PAGE')
                  | translate : { page: item.value }
              "
              [attr.aria-current]="vm.currentPageIndex + 1 === item.value"
              (click)="onPageClick(item.value)"
            >
              {{ item.value }}
            </button>

            <ng-template #ellipses>
              <span>&hellip;</span>
            </ng-template>
          </li>
        </ng-container>

        <li>
          <button
            [attr.aria-label]="'LIBS.PAGINATION.ARIA_LABELS.NEXT_PAGE' | translate"
            [disabled]="vm.currentPageIndex + 1 >= vm.numberOfPages"
            (click)="onNextClick(vm.currentPageIndex)"
          >
            <tsq-icon [icon]="chevronIcon" classes="size-24" />
          </button>
        </li>
      </ul>
    </nav>
  `,
  styles: [
    `
      :host > nav {
        button,
        span {
          @apply text-coal-secondary flex h-24 items-center justify-center rounded px-8 font-bold;
        }

        button {
          @apply transition-colors;

          &[disabled] {
            @apply text-coal-tertiary bg-petro-n1 cursor-not-allowed;
          }

          &:focus:not(:focus-visible) {
            outline: none;
          }
        }

        &[schema='new-primary'] button {
          &:hover:not([disabled]) {
            @apply bg-new-primary-n3 text-new-primary;
          }

          &[aria-current='true'] {
            @apply text-new-primary-p1;
          }

          &:focus-visible {
            outline: theme('colors.purple.DEFAULT') dotted 2px;
            outline-offset: 0;
          }
        }

        &[schema='primary'] button {
          &:hover:not([disabled]) {
            @apply bg-primary-n1 text-primary;
          }

          &[aria-current='true'] {
            @apply text-primary-p1;
          }

          &:focus-visible {
            @apply outline outline-offset-2;
          }
        }
      }
    `,
  ],
})
export class PaginationComponent {
  @Input() schema: 'new-primary' | 'primary' = 'new-primary';

  @Output() readonly pageIndexChange = new EventEmitter<number>();

  protected readonly chevronIcon = icons.chevron;

  private readonly totalSubject = new BehaviorSubject<number>(0);
  private readonly pageSizeSubject = new BehaviorSubject<number>(10);
  protected readonly numberOfPages$ = combineLatest([
    this.totalSubject.asObservable(),
    this.pageSizeSubject.asObservable(),
  ]).pipe(
    map(([total, pageSize]) => {
      return Math.ceil(total / pageSize);
    }),
  );

  private readonly queryParams$ = inject(ActivatedRoute).queryParams;
  private readonly routeBasedPaginationOptOutSubject = new BehaviorSubject<boolean>(false);
  private readonly localStoredPageSubject = new BehaviorSubject<number>(0);
  protected readonly currentPageIndex$ = this.routeBasedPaginationOptOutSubject.asObservable().pipe(
    mergeMap(routeBasedPaginationOptOut => {
      if (routeBasedPaginationOptOut) {
        return this.localStoredPageSubject.asObservable();
      }

      return this.queryParams$.pipe(
        mergeMap(queryParams => {
          return this.pageParamNameSubject.asObservable().pipe(
            map(pageParamName => {
              return pageIndexFromParams(queryParams, pageParamName);
            }),
          );
        }),
      );
    }),
  );

  protected readonly visibleItems$ = combineLatest([
    this.numberOfPages$,
    this.currentPageIndex$,
  ]).pipe(
    map(([numberOfPages, pageIndex]) => {
      return calculateVisiblePaginationItems(pageIndex + 1, numberOfPages);
    }),
  );

  private readonly pageParamNameSubject = new BehaviorSubject<string>('p');

  private readonly updateQueryParams = updateQueryParamsV2();

  constructor() {
    this.currentPageIndex$
      .pipe(
        skipWhile(() => {
          return !this.routeBasedPaginationOptOutSubject.value;
        }),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe(currentPageIndex => {
        this.pageIndexChange.emit(currentPageIndex);
      });
  }

  @Input()
  set total(value: number) {
    this.totalSubject.next(value >= 0 ? value : 0);
  }

  @Input()
  set pageSize(value: number) {
    this.pageSizeSubject.next(value >= 0 ? value : 10);
  }

  @Input()
  set pageParamName(value: string) {
    this.pageParamNameSubject.next(!!value ? value : 'p');
  }

  @Input()
  set routeBasedPaginationOptOut(value: boolean) {
    this.routeBasedPaginationOptOutSubject.next(value);
  }

  @Input()
  set pageIndex(value: number) {
    this.localStoredPageSubject.next(value);
  }

  goToPageIndex(pageIndex: number): void {
    this.updatePageIndex(pageIndex);
  }

  protected onPreviousClick(pageIndex: number): void {
    this.updatePageIndex(pageIndex - 1);
  }

  protected onNextClick(pageIndex: number): void {
    this.updatePageIndex(pageIndex + 1);
  }

  protected onPageClick(page: number): void {
    this.updatePageIndex(page - 1);
  }

  protected trackByPaginationItem(index: number, item: PaginationItem): number {
    return item.type === 'page' ? item.value : index;
  }

  private updatePageIndex(pageIndex: number): void {
    if (this.routeBasedPaginationOptOutSubject.value) {
      this.localStoredPageSubject.next(pageIndex);
    } else {
      this.updateQueryParams({ [this.pageParamNameSubject.value]: pageIndex });
    }
  }
}
